/**
 * Browser-MCP availability — single source of truth for whether the
 * chrome-devtools browser-control MCP is usable, decoupled from any one SDK
 * query's frozen deferred-tools snapshot.
 *
 * Why this exists (bug⑬b root cause):
 *   When ENABLE_TOOL_SEARCH is on (Claude 主脑), the SDK defers every MCP tool
 *   into a ToolSearch index. chrome-devtools runs as a process-internal SDK
 *   proxy backed by ONE shared connection-manager child. That manager's readiness
 *   changes at runtime (crash/recover, Chrome debug-port timing). If the provider
 *   does not include chrome-devtools at the moment a turn's session is built, the
 *   tools never enter that turn's index and every ToolSearch returns "No matching
 *   deferred tools found" for the rest of that session — until the session is
 *   rebuilt / reopened and re-reads the provider.
 *
 *   This module is the pure, testable derivation shared across layers: the
 *   desktop main process (holds connection manager + install state) feeds live
 *   inputs; the UI pill reads the derived state. Lives in @dosia/shared so both
 *   core (desktop main) and ui (the pill) reference ONE definition — ui must not
 *   depend on @dosia/core (would invert shared ← core ← ui).
 *
 * B1 (2026-08-08, corrected 2026-08-11): `chromeDebugReady` means a
 * **verified live MCP browser connection**, not mere DevToolsActivePort file
 * existence. In Chrome 144+ permission-proxy mode `/json/version` may return
 * 404 by design, so readiness is proven through the shared MCP `list_pages`.
 */
/**
 * Canonical MCP server name for the browser-control MCP = the tool prefix the
 * model expects (`mcp__chrome-devtools__*`). Decoupled from the npm package
 * identity `chrome-devtools-mcp`. Single source of truth (core's catalog entry
 * `serverName` + the manager proxy name + the pill's mount check all trace
 * here). Re-exported by @dosia/core's mcp-registry catalog for back-compat.
 */
export const CHROME_DEVTOOLS_SERVER_NAME = "chrome-devtools";
/**
 * 浏览器 MCP 的上游 npm 包名，也是 DOSIA 安装目录 / 可执行文件 / 设置面安装行的名字
 * （core 目录条目的 `name` / `npmPackage` / `bin` 同值）。
 */
const CHROME_DEVTOOLS_MCP_PACKAGE = "chrome-devtools-mcp";
/**
 * 只属于 DOSIA 浏览器的连接器名（B-273）。普通对话的浏览器只走输入框下方的「浏览器」按钮
 * （维护者 2026-10-24 拍板）：员工自建连接器不得占用这些名字，会话装配也丢弃非 DOSIA 来源的
 * 同名条目。判据见 {@link isReservedBrowserMcpEntry}。
 */
export const RESERVED_BROWSER_MCP_NAMES = [
    CHROME_DEVTOOLS_SERVER_NAME,
    CHROME_DEVTOOLS_MCP_PACKAGE,
];
/** 包规格：上游包名（可带 `@版本`），或同名 scoped 包（几乎恒为上游镜像 / fork，跑起来同样是另一份浏览器）。 */
const PACKAGE_SPEC = /^(?:@[^/\\\s@]+\/)?chrome-devtools-mcp(?:@[^/\\\s@]+)?$/i;
/** 可执行入口的文件名：全局 bin、Windows 包装、上游的入口脚本。 */
const ENTRY_FILE = /^chrome-devtools-mcp(?:\.(?:[cm]?js|cmd|exe|ps1))?$/i;
const ENTRY_FILE_WITH_EXT = /^chrome-devtools-mcp\.(?:[cm]?js|cmd|exe|ps1)$/i;
const JS_FILE = /\.[cm]?js$/i;
/** 后面第一个位置参数就是要跑的脚本的命令。 */
const JS_RUNTIMES = new Set(["node", "nodejs", "bun"]);
function pathSegments(token) {
    return token.split(/[\\/]/).filter(Boolean);
}
function basenameOf(token) {
    const segs = pathSegments(token);
    return segs[segs.length - 1] ?? "";
}
/**
 * 这个值是不是上游的入口脚本：文件名就是带扩展名的入口（`chrome-devtools-mcp.js` / `.cmd` …），
 * 或是包目录 `chrome-devtools-mcp/build/src/` 下的 JS（上游发布布局，老版本入口 `build/src/index.js`）。
 * 只是包目录、或包目录里别的文件（profile、配置）都不算。
 */
function isEntryScriptPath(value) {
    const segs = pathSegments(value);
    const base = segs[segs.length - 1];
    if (!base)
        return false;
    if (ENTRY_FILE_WITH_EXT.test(base))
        return true;
    if (!JS_FILE.test(base))
        return false;
    for (let i = 0; i + 3 < segs.length; i += 1) {
        if (segs[i].toLowerCase() === "chrome-devtools-mcp"
            && segs[i + 1] === "build"
            && segs[i + 2] === "src") {
            return true;
        }
    }
    return false;
}
/**
 * 本地命令型条目跑的是不是上游 chrome-devtools-mcp——只认「被运行的那个东西」所在的位置：
 * - 命令本身：文件名是 `chrome-devtools-mcp`（可带 `.js/.mjs/.cjs/.cmd/.exe/.ps1`），或是入口脚本路径；
 * - 不以 `-` 开头的独立参数：整串是包规格（`chrome-devtools-mcp@latest`、同名 scoped 包），或是入口脚本路径；
 *   npx / bunx / pnpm dlx / `cmd /c npx` 都落在这一条；
 * - 命令是 node / bun 时，它后面第一个位置参数的文件名就是 `chrome-devtools-mcp`（全局 bin 无扩展名）；
 * - `--flag=值`：只有值本身是入口脚本路径才算。
 * 只是把上游目录或包名当成某个选项的值（`--user-data-dir=…/chrome-devtools-mcp`、文件系统连接器的根目录）
 * 不算——那不是被运行的东西（审查 #4264）。
 */
function stdioRunsChromeDevtoolsMcp(command, args) {
    const cmd = command?.trim() ?? "";
    if (cmd && (ENTRY_FILE.test(basenameOf(cmd)) || isEntryScriptPath(cmd)))
        return true;
    const runtime = JS_RUNTIMES.has(basenameOf(cmd).toLowerCase().replace(/\.exe$/, ""));
    let firstPositional = true;
    for (const raw of args) {
        const arg = raw.trim();
        if (!arg)
            continue;
        if (arg.startsWith("-")) {
            const eq = arg.indexOf("=");
            if (eq > 0 && isEntryScriptPath(arg.slice(eq + 1)))
                return true;
            continue;
        }
        if (PACKAGE_SPEC.test(arg) || isEntryScriptPath(arg))
            return true;
        if (runtime && firstPositional && basenameOf(arg).toLowerCase() === "chrome-devtools-mcp")
            return true;
        firstPositional = false;
    }
    return false;
}
/**
 * 判据单源（B-273）：这个连接器条目是不是「另一份 DOSIA 浏览器」——名字是保留名（不分大小写），
 * 或是本地命令型（stdio，`type` 缺省也按 stdio 看）而它运行的就是上游 chrome-devtools-mcp
 * （位置判据见 stdioRunsChromeDevtoolsMcp）。远程 http/sse 只看名字。
 *
 * 两个消费点读这一份：创建口 `buildConnectorEntry`（设置表单与 propose_connector 共用）拒建；
 * core 会话装配（resolveMcpServers 的 reserved 选项）丢弃非 DOSIA 来源的命中条目。`entry` 是不可信
 * 的落库数据，形状不对一律判否、不抛错。
 *
 * 不判的形状（都跑不起来，不会成为对话里的浏览器）：整串写进 `command` 一个字段、`type` 大写、
 * 参数自带引号。整条命令塞进 `sh -c "<字符串>"` 的也不判——字符串里的词与自由文本分不开，判了会误伤。
 */
export function isReservedBrowserMcpEntry(name, entry) {
    const lower = name.trim().toLowerCase();
    if (RESERVED_BROWSER_MCP_NAMES.includes(lower))
        return true;
    if (!entry || typeof entry !== "object")
        return false;
    const e = entry;
    if (e.type !== undefined && e.type !== "stdio")
        return false;
    const command = typeof e.command === "string" ? e.command : undefined;
    const args = Array.isArray(e.args) ? e.args.filter((a) => typeof a === "string") : [];
    return stdioRunsChromeDevtoolsMcp(command, args);
}
/**
 * Chrome 自己对一次 `evaluate_script` 页面脚本的上限（B-244）：随包 chrome-devtools-mcp 1.5.0
 * 连接 Chrome 时不设 puppeteer `protocolTimeout`，落到 puppeteer `Connection` 的默认 `180_000`，
 * 超过即上游报 `Runtime.callFunctionOn timed out`。维护者 2026-10-16 拍板 DOSIA 不先于 Chrome 放弃。
 *
 * 两个消费方读同一个值：core 的 facade 单次等待上限（agent/browser-page-lifecycle.ts，此值 + 余量）
 * 与 facade 追加给模型的 `evaluate_script` 说明（tools/mcp-client-proxy.ts）。
 */
export const CHROME_EVALUATE_SCRIPT_MAX_MS = 180_000;
/**
 * chrome-devtools 里只读取页面 / 浏览器状态、不写任何东西的工具名（B-117）。
 *
 * 唯一消费方：进程内 facade 的只读兜底——上游 `tools/list` 没给 `readOnlyHint` 时按本名单补
 * （core tools/mcp-client-proxy.ts），上游明确给了的以上游为准。「只读」会让 pi 腿权限门
 * 恒放行，所以带 `filePath` 参数、能写本地文件的 `take_snapshot` / `take_screenshot` /
 * `get_network_request` 不在这里（与随包 chrome-devtools-mcp 1.5.0 自己的 `readOnlyHint`
 * 一致），它们只在下面的 {@link CHROME_DEVTOOLS_IDEMPOTENT_TOOLS}。
 */
export const CHROME_DEVTOOLS_READ_ONLY_TOOLS = new Set([
    "list_pages",
    "select_page",
    "get_tab_id",
    "wait_for",
    "get_console_message",
    "list_console_messages",
    "list_network_requests",
    "performance_analyze_insight",
]);
/**
 * chrome-devtools 里「同参数再调一次不会多产生任何效果」的工具名（B-117）= 上面的只读名单
 * 加三只会按 `filePath` 写本地文件的读工具（同参数重写同一文件，仍是幂等）。
 *
 * 消费方都引用这一份，不另抄：
 *   - 风险分级 L0 档 `chrome-devtools-read`（core policies/risk-level-registry.ts；
 *     截图 / 快照只落本机，一直按 L0 登记）；
 *   - 超时 / 页面重绑时「可安全再调一次」的判据（core agent/browser-self-heal.ts
 *     `isIdempotentChromeTool`，`navigate_page` 另按参数判；agent/browser-page-lifecycle.ts）；
 *   - 进程内 facade 的幂等兜底：上游没给 `idempotentHint` 时按本名单补 `true`
 *     （core tools/mcp-client-proxy.ts），pi 腿据此不把报错改写成「状态未知」、不冻结同名重调。
 *     幂等**不**进权限门的只读地板。
 *
 * 会改页面或会触发页面脚本的工具（click / fill / navigate_page / evaluate_script …）
 * 一律不进——不在名单 = 按有副作用处理（fail-closed）。
 */
export const CHROME_DEVTOOLS_IDEMPOTENT_TOOLS = new Set([
    ...CHROME_DEVTOOLS_READ_ONLY_TOOLS,
    "take_snapshot",
    "take_screenshot",
    "get_network_request",
]);
/**
 * chrome-devtools 里 `pageId` 是「操作对象」而不是「在哪一页上做」的工具名：选哪一页、关哪一页、
 * 查哪一页的标签号（随包 chrome-devtools-mcp 1.5.0 pages.js 里用 `defineTool` 自带 pageId 入参的
 * 三只；其余带 pageId 的都是 `--experimentalPageIdRouting` 加给页面工具的路由参数）。
 *
 * 消费方都引用这一份，不另抄：
 *   - 页面被关后不换页重跑（core agent/browser-page-lifecycle.ts）——换页 = 选错页 / 关错页；
 *   - 缺 pageId 时不按本对话当前页补位（core agent/browser-page-binding.ts）——补位 = 替模型
 *     选定「关哪一页」。
 */
export const CHROME_DEVTOOLS_PAGE_OPERAND_TOOLS = new Set([
    "select_page",
    "close_page",
    "get_tab_id",
]);
/** Map connection-manager lifecycle → coarse singleton projection. */
export function mapLifecycleToSingletonState(lifecycle) {
    switch (lifecycle) {
        case "ready":
            return "ready";
        case "preflighting":
        case "launching-browser":
        case "starting-mcp":
        case "probing-cdp":
        case "recovering":
            return "starting";
        case "degraded":
        case "failed":
            return "crashed";
        case "awaiting-user-action":
        case "stopped":
        default:
            return "idle";
    }
}
/** Whether a health code means the coarse UI should treat CDP as ready. */
export function isChromeDebugReadyFromHealth(healthCode) {
    return healthCode === "ready";
}
/**
 * i18n key for a structured health tip (7 locales under chat.browserHealth.*).
 * Falls back to the generic needs-setup tip when unknown/null.
 */
export function browserHealthI18nKey(healthCode) {
    switch (healthCode) {
        case "needs-enable-remote-debugging":
            return "chat.browserHealth.needsEnableRemoteDebugging";
        case "needs-restart-chrome":
            return "chat.browserHealth.needsRestartChrome";
        case "needs-allow-debugging-connection":
            return "chat.browserPill.needsSetupTip";
        case "permission-denied":
            return "chat.browserHealth.permissionDenied";
        case "profile-conflict":
            return "chat.browserHealth.profileConflict";
        case "endpoint-stale":
            return "chat.browserHealth.endpointStale";
        case "mcp-start-failed":
            return "chat.browserHealth.mcpStartFailed";
        case "cdp-timeout":
            return "chat.browserHealth.cdpTimeout";
        case "page-timeout":
            return "chat.browserHealth.pageTimeout";
        case "ready":
            return "chat.browserPill.connectedTip";
        default:
            return "chat.browserPill.needsSetupTip";
    }
}
/**
 * Derive the single availability state. Pure — the desktop main process feeds
 * live inputs (install-state store + connection-manager lifecycle) and this
 * decides the one truth every consumer reads.
 *
 * `starting` is reserved for a REAL in-flight recover/start — the only state
 * where the tool genuinely isn't callable yet. Both `idle` (never started —
 * manager lazy-inits on first browser-tool call) and `crashed` (auto recovers
 * on next use when possible) collapse based on `chromeDebugReady`:
 * - debug ready → `ready` (lazy path works)
 * - debug not ready → `needs-setup` (do not pretend connected)
 *
 * B1 correction: file existence alone must NOT drive ready. Manager sets
 * `chromeDebugReady` only after the shared MCP succeeds at `list_pages`.
 */
export function deriveBrowserMcpAvailability(i) {
    if (!i.installed)
        return "unavailable";
    // 没装（能力不存在）压过用户开关——一个没有 chrome-devtools 的构建里，这个开关是空的；
    // 除此之外用户的显式选择压过一切运行时健康信号（starting / healthCode / chromeDebugReady）。
    if (i.automationEnabled === false)
        return "disabled";
    if (i.singleton === "starting")
        return "starting";
    // Explicit health overrides: awaiting user action / connection failures
    // are never "ready" even if singleton projects idle/crashed with a stale flag.
    if (i.healthCode && i.healthCode !== "ready" && i.healthCode !== "page-timeout") {
        if (i.healthCode === "needs-enable-remote-debugging" ||
            i.healthCode === "needs-restart-chrome" ||
            i.healthCode === "needs-allow-debugging-connection" ||
            i.healthCode === "permission-denied" ||
            i.healthCode === "profile-conflict" ||
            i.healthCode === "endpoint-stale" ||
            i.healthCode === "mcp-start-failed" ||
            i.healthCode === "cdp-timeout") {
            return "needs-setup";
        }
    }
    if (!i.chromeDebugReady)
        return "needs-setup";
    return "ready";
}
export function deriveBrowserMcpPillState(i) {
    // 总开关是整机级事实，比「这个会话订没订阅」「这个 Agent 声没声明」更靠外；不排最前的话，
    // 一个绑了 Agent 的对话在总开关关闭时会显示「此 Agent 无浏览器」——把机器级原因说成
    // Agent 配置问题，员工会去找管理员改 Agent，白跑一趟。
    if (i.availability === "disabled")
        return "global-off";
    // 订阅制门控优先于其余全局 availability：问的是"THIS 会话要不要"，不是"chrome-devtools
    // 装没装/连没连"——那是完全独立的另一个问题。
    if (i.sessionState === "unsubscribed")
        return "hidden";
    if (i.availability === "unavailable")
        return "hidden";
    if (i.availability === "starting")
        return "starting";
    if (i.availability === "needs-setup")
        return "needs-setup";
    // availability === "ready": only a session whose built query is missing the
    // tools needs a remount; unbuilt (next message picks it up) + mounted are both
    // "ready" to the user.
    return i.sessionState === "missing" ? "needs-remount" : "connected";
}
/**
 * Mid-session interruption for the in-chat reconnect banner + pill wording
 * (B-206, employee-picked banner 2026-10-16). First-time setup (never ready in
 * this run), the user's global switch, a missing install and a ready connection
 * are not interruptions. The health code picks what the employee must do.
 */
export function deriveBrowserMcpInterruption(i) {
    if (!i.everReady)
        return null;
    if (i.availability === "disabled" || i.availability === "unavailable" || i.availability === "ready") {
        return null;
    }
    if (i.availability === "starting" || i.singleton === "starting")
        return "reconnecting";
    switch (i.healthCode) {
        case "needs-allow-debugging-connection":
            return "needs-consent";
        case "needs-enable-remote-debugging":
        case "needs-restart-chrome":
        case "endpoint-stale":
        case "permission-denied":
        case "profile-conflict":
            return "needs-chrome";
        default:
            return "failed";
    }
}
export function deriveBrowserSettingsCardState(i) {
    if (!i.automationEnabled)
        return "off";
    if (!i.chromePresent)
        return "no-chrome";
    if (i.availability === "ready")
        return "ready";
    // 有意的塌缩：随包构建里 chrome-devtools 恒存在、Chrome 也在，剩下唯一还没满足的前置
    // 就是「用户还没在 Chrome 弹出的框里点允许」。更细的原因由既有
    // `browserHealthI18nKey(healthCode)` 出提示行，不由本函数区分。
    // `disabled` 落到这一格是不可达兜底（第 1 条已拦）。
    return "awaiting-allow";
}
//# sourceMappingURL=browser-mcp-availability.js.map